import type { KeyboardEvent, PointerEvent, ReactNode } from "react";
import { CheckIcon, DownloadIcon, PauseIcon, PlayIcon } from "./icons";
import { cn, focusRing } from "./utils";

// Bar heights for a waveform. Real audio will supply its own peaks.
const DEFAULT_PEAKS = [8, 14, 20, 12, 24, 18, 10, 22, 16, 26, 12, 8, 18, 22, 14, 10, 20, 12, 16, 8, 6];

export function Waveform({
  peaks = DEFAULT_PEAKS,
  progress = 0,
  className,
}: {
  peaks?: number[];
  progress?: number;
  className?: string;
}) {
  const played = Math.round(peaks.length * progress);
  return (
    <span className={cn("flex h-[26px] items-center gap-0.5", className)} aria-hidden="true">
      {peaks.map((h, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional
          key={i}
          className={cn("w-[3px] rounded-[3px]", i < played ? "bg-blue" : "bg-wave")}
          style={{ height: h }}
        />
      ))}
    </span>
  );
}

export function VoiceNote({
  position,
  duration,
  time,
  progress = 0,
  speed = "1×",
  peaks,
  playing = false,
  onPlay,
  onSeek,
  onSpeed,
}: {
  position: string;
  duration: string;
  time: string;
  progress?: number;
  speed?: string;
  peaks?: number[];
  playing?: boolean;
  onPlay?: () => void;
  /** Called with a fraction from 0 to 1 when the person drags or steps through the waveform. */
  onSeek?: (fraction: number) => void;
  onSpeed?: () => void;
}) {
  const seekAt = (e: PointerEvent<HTMLSpanElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width > 0) onSeek?.(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };
  const onKey = (e: KeyboardEvent<HTMLSpanElement>) => {
    const step = { ArrowRight: 0.05, ArrowUp: 0.05, ArrowLeft: -0.05, ArrowDown: -0.05 }[e.key];
    if (e.key === "Home") onSeek?.(0);
    else if (e.key === "End") onSeek?.(1);
    else if (step === undefined) return;
    else onSeek?.(Math.min(1, Math.max(0, progress + step)));
    e.preventDefault();
  };
  return (
    <div className="flex items-center gap-2.5 py-[9px] pr-[13px] pl-[9px]">
      <button
        type="button"
        aria-label={playing ? "Pause voice message" : "Play voice message"}
        onClick={onPlay}
        className={cn(
          "flex h-[38px] w-[38px] flex-none cursor-pointer items-center justify-center rounded-full bg-blue text-white",
          focusRing,
        )}
      >
        {playing ? <PauseIcon size={15} /> : <PlayIcon size={15} />}
      </button>
      <span className="flex flex-col gap-1">
        <span
          role="slider"
          tabIndex={0}
          aria-label="Playback position"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          aria-valuetext={`${position} of ${duration}`}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture?.(e.pointerId);
            seekAt(e);
          }}
          onPointerMove={(e) => {
            if (e.buttons) seekAt(e);
          }}
          onKeyDown={onKey}
          className={cn("cursor-pointer touch-none rounded-md", focusRing)}
        >
          <Waveform peaks={peaks} progress={progress} />
        </span>
        <span className="flex justify-between text-[11px] text-ink-meta">
          <span>
            {position} / {duration}
          </span>
          <span>{time}</span>
        </span>
      </span>
      <button
        type="button"
        aria-label={`Playback speed ${speed}`}
        onClick={onSpeed}
        className={cn(
          "min-w-[42px] cursor-pointer rounded-full bg-surface-header px-2 py-1 text-xs font-bold text-ink",
          focusRing,
        )}
      >
        {speed}
      </button>
    </div>
  );
}

export function LinkPreview({ site, title, summary }: { site: string; title: string; summary?: string }) {
  return (
    <div className="overflow-hidden rounded-field bg-linen px-3 pt-[9px] pb-2.5 text-ink">
      <div className="text-[11.5px] text-ink-meta">{site}</div>
      <div className="font-serif text-base leading-tight font-semibold">{title}</div>
      {summary && <div className="text-[12.5px] leading-snug text-ink-secondary">{summary}</div>}
    </div>
  );
}

/** Badge colours for a file's type. PDFs are gold, as on the design board, and the rest blue. */
function badgeTone(kind: string): string {
  return kind === "PDF" ? "bg-gold-tint text-gold-ink" : "bg-blue-tint text-blue";
}

export function FileCard({
  name,
  detail,
  kind = "PDF",
  onDownload,
  onOpen,
  tone = "default",
  action,
}: {
  name: string;
  detail: string;
  kind?: string;
  /** Saves the file. Without it there is no save button. */
  onDownload?: () => void;
  /** Opens the file to look at, where Rhema can show it. */
  onOpen?: () => void;
  /** "error" shows the detail line in red, as for a file that was not sent. */
  tone?: "default" | "error";
  /** Shown in place of Open and Save, such as a Retry button. */
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-field bg-chat p-2.5">
      <span
        className={cn(
          "flex h-11 w-10 flex-none items-center justify-center rounded-lg text-[10px] font-bold",
          badgeTone(kind),
        )}
      >
        {kind}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="line-clamp-2 text-sm font-semibold [overflow-wrap:anywhere]">{name}</span>
        <span className={cn("text-xs", tone === "error" ? "text-scarlet" : "text-ink-meta")}>{detail}</span>
      </span>
      {action}
      {!action && onOpen && (
        <button
          type="button"
          aria-label={`Open ${name}`}
          onClick={onOpen}
          className={cn(
            "h-[34px] flex-none cursor-pointer rounded-full border border-line bg-surface px-3 text-[13px] font-semibold text-ink",
            focusRing,
          )}
        >
          Open
        </button>
      )}
      {!action && onDownload && (
        <button
          type="button"
          aria-label={`Save ${name}`}
          title="Save"
          onClick={onDownload}
          className={cn(
            "flex h-[34px] w-[34px] flex-none cursor-pointer items-center justify-center rounded-full border border-line bg-surface text-ink",
            focusRing,
          )}
        >
          <DownloadIcon size={15} strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

export type PollOption = { id: string; label: string; votes: number; chosen?: boolean };

export function PollCard({
  question,
  options,
  summary,
  multiple = false,
  onVote,
  onViewVotes,
}: {
  question: string;
  options: PollOption[];
  summary: string;
  /** People can choose more than one option. */
  multiple?: boolean;
  onVote?: (id: string) => void;
  onViewVotes?: () => void;
}) {
  const total = options.reduce((n, o) => n + o.votes, 0) || 1;
  const leader = Math.max(...options.map((o) => o.votes));
  return (
    <div>
      <div className="mt-0.5 font-serif text-lg font-semibold leading-snug">{question}</div>
      <div className="mt-0.5 mb-2.5 text-xs text-ink-meta">
        {multiple ? "Poll · choose one or more" : "Poll · choose one"}
      </div>
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {options.map((o) => (
          <li key={o.id}>
            <button
              type="button"
              aria-pressed={Boolean(o.chosen)}
              onClick={() => onVote?.(o.id)}
              className={cn(
                "flex w-full cursor-pointer flex-col gap-[5px] rounded-md text-left text-sm font-medium",
                focusRing,
              )}
            >
              <span className="flex w-full items-center gap-2">
                {o.chosen ? (
                  <span
                    className={cn(
                      "flex h-5 w-5 items-center justify-center bg-blue text-white",
                      multiple ? "rounded-md" : "rounded-full",
                    )}
                  >
                    <CheckIcon size={12} strokeWidth={4} />
                  </span>
                ) : (
                  <span
                    className={cn(
                      "box-border h-5 w-5 border-2 border-line-strong",
                      multiple ? "rounded-md" : "rounded-full",
                    )}
                  />
                )}
                <span className="flex-1">{o.label}</span>
                <span className="font-bold">
                  {o.votes}
                  <span className="sr-only"> votes</span>
                </span>
              </span>
              <span className="ml-7 block h-1.5 w-[calc(100%-28px)] rounded-md bg-surface-header">
                {o.votes > 0 && (
                  <span
                    className={cn("block h-1.5 rounded-md", o.votes === leader ? "bg-blue" : "bg-ink-muted")}
                    style={{ width: `${(o.votes / total) * 100}%` }}
                  />
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onViewVotes}
        className={cn(
          "mt-2.5 h-10 w-full cursor-pointer border-0 border-t border-line bg-transparent text-sm font-semibold text-ink",
          focusRing,
        )}
      >
        {summary}
      </button>
    </div>
  );
}

export function LocationCard({
  map,
  title,
  detail,
  onView,
  href,
  actionLabel = "View",
}: {
  map: ReactNode;
  title: string;
  detail: string;
  onView?: () => void;
  /** Opens in a new tab, such as a maps site. */
  href?: string;
  actionLabel?: string;
}) {
  const action = cn(
    "flex h-[34px] flex-none cursor-pointer items-center rounded-full bg-surface-header px-3.5 text-[13px] font-semibold text-ink no-underline",
    focusRing,
  );
  return (
    <div>
      <div className="overflow-hidden rounded-field">{map}</div>
      <div className="flex items-center gap-2 px-[5px] pt-2">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-semibold">{title}</span>
          <span className="text-xs text-ink-meta">{detail}</span>
        </span>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className={action}>
            {actionLabel}
          </a>
        ) : (
          <button type="button" onClick={onView} className={action}>
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
